import { useEffect, useState } from 'react';
import { dataService } from '@/services/dataService';
import { SystemService } from '@/types/entities';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { ENV } from '@/config/env';
import { DATA_MODE } from '@/config/dataMode';
import { AI_PROVIDERS } from '@/config/aiProviders.config';
import { useI18n } from '@/i18n';

/**
 * Every number on this page is computed from real local state — connector
 * count from the actual registry, AI provider count from the actual
 * config, browser/language/theme from the actual runtime. Nothing here is
 * a fabricated "system health" green light; services genuinely not wired
 * to a backend stay honestly "Not Connected" via listSystemServices().
 */
export function SystemPage() {
  const { t, lang } = useI18n();
  const [services, setServices] = useState<SystemService[]>([]);
  const [connectorCount, setConnectorCount] = useState<number | null>(null);
  const [loadedAt] = useState(() => new Date().toLocaleString());

  useEffect(() => {
    dataService.listSystemServices().then(setServices);
    dataService.listConnectorStatuses().then((c) => setConnectorCount(c.length));
  }, []);

  const aiProviderCount = Object.keys(AI_PROVIDERS).length;
  const configuredProviderCount = Object.values(AI_PROVIDERS).filter((p) => p.configured).length;
  const theme = document.documentElement.getAttribute('data-theme') ?? 'light';

  return (
    <div>
      <h1>{t('nav.system')}</h1>
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, fontSize: 13 }}>
          <div><div style={{ color: 'var(--text-3)' }}>{t('system.environment')}</div><div style={{ fontWeight: 700 }}>{t(`env.${ENV.environment}`)}</div></div>
          <div><div style={{ color: 'var(--text-3)' }}>{t('system.version')}</div><div style={{ fontWeight: 700 }}>{ENV.version}</div></div>
          <div><div style={{ color: 'var(--text-3)' }}>{t('system.build')}</div><div style={{ fontWeight: 700 }}>{ENV.buildTime}</div></div>
          <div><div style={{ color: 'var(--text-3)' }}>Data mode</div><div style={{ fontWeight: 700 }}>{DATA_MODE === 'demo' ? t('common.demoData') : DATA_MODE}</div></div>
          <div><div style={{ color: 'var(--text-3)' }}>Connectors registered</div><div style={{ fontWeight: 700 }}>{connectorCount ?? '…'}</div></div>
          <div><div style={{ color: 'var(--text-3)' }}>AI providers configured</div><div style={{ fontWeight: 700 }}>{configuredProviderCount} / {aiProviderCount}</div></div>
          <div><div style={{ color: 'var(--text-3)' }}>Language</div><div style={{ fontWeight: 700 }}>{lang === 'ar' ? 'العربية (RTL)' : 'English (LTR)'}</div></div>
          <div><div style={{ color: 'var(--text-3)' }}>Theme</div><div style={{ fontWeight: 700 }}>{theme}</div></div>
          <div><div style={{ color: 'var(--text-3)' }}>App loaded</div><div style={{ fontWeight: 700 }}>{loadedAt}</div></div>
          <div style={{ gridColumn: '1 / -1' }}><div style={{ color: 'var(--text-3)' }}>Browser</div><div style={{ fontWeight: 700, wordBreak: 'break-all' }}>{navigator.userAgent}</div></div>
        </div>
      </div>
      <div className="card" style={{ padding: 8 }}>
        <div className="table-scroll">
        <table className="dt">
          <thead><tr><th>Service</th><th>Status</th><th>Detail</th></tr></thead>
          <tbody>
            {services.map((s) => (
              <tr key={s.key}>
                <td>{s.displayName}</td>
                <td><StatusBadge entity="system" statusKey={s.status} /></td>
                <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{s.detail}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
